import { useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { trackEvent } from "../lib/analytics.js"
import { loginWithGoogle } from "../lib/auth.js"
import styles from "./LoginModal.module.css"

interface Props {
  open: boolean
  onClose: () => void
  returnTo?: string
}

// A cushion step before handing off to Google's OAuth page, rather than
// navigating away the instant "ログイン" is clicked — lets the user confirm
// intent and see a familiar "Sign in with Google" button first.
export function LoginModal({ open, onClose, returnTo }: Props) {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    trackEvent("login_modal_opened")

    document.body.style.overflow = "hidden"
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onCloseRef.current()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.body.style.overflow = ""
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  if (!open) return null

  // Rendered into document.body rather than in place: an ancestor with any
  // CSS transform (e.g. BenefitCard's :hover lift) becomes a containing
  // block for position:fixed descendants, which broke the full-viewport
  // backdrop when this modal was opened from the star button inside a card.
  return createPortal(
    <div className={styles.root}>
      <button type="button" className={styles.backdrop} aria-label="閉じる" onClick={onClose} />
      <div
        className={styles.dialog}
        // biome-ignore lint/a11y/useSemanticElements: native <dialog> lacks the backdrop/animation control this needs
        role="dialog"
        aria-modal="true"
        aria-label="ログイン"
      >
        <button type="button" className={styles.closeButton} aria-label="閉じる" onClick={onClose}>
          ×
        </button>
        <p className={styles.description}>
          ログインすると銘柄のお気に入り登録など便利な機能が使えるようになります。
        </p>
        <button
          type="button"
          className={styles.googleButton}
          onClick={() => loginWithGoogle(returnTo)}
        >
          <svg className={styles.googleIcon} viewBox="0 0 18 18" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62z"
            />
            <path
              fill="#34A853"
              d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 0 0 9 18z"
            />
            <path
              fill="#FBBC05"
              d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.7V4.97H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.03l2.99-2.33z"
            />
            <path
              fill="#EA4335"
              d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.97l2.99 2.33C4.66 5.17 6.65 3.58 9 3.58z"
            />
          </svg>
          Googleでログイン
        </button>
      </div>
    </div>,
    document.body
  )
}
